<template>
  <div :class="sectionClass" @mouseover="onMouseOver" @mouseleave="onMouseLeave">
    <a v-if="section.title" class="section-header" :href="section.href" @click="onClick(section)">
      <lucide-icon :name="section.icon" :stroke-width="2" />
      <div :class="`t m-l12 ${isOpen ? 'black' : ''}`">
        <span v-for="(part, idx) in titleParts" :key="idx"><br v-if="idx > 0">{{ part }}</span>
      </div>
      <span v-if="section.badge && !isOpen" :class="badgeClass(section.badge, 'm-t4')">{{ badges[section.badge].text }}</span>
      <i v-if="canOpenOnHover && !isOpen" class="mi md-18 m-l">keyboard_arrow_down</i>
    </a>
    <transition name="section-expand" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
      <div v-if="isOpen" class="f-v f-as m-l12 p-lx3 m-t5">
        <a v-for="(link, idx) in section.links" :key="`link-${idx}`" :href="link.href" :class="linkClass(link, idx, section.id)" @click="onClick(link)">
          <div class="t">{{ link.title }}</div>
          <span v-if="link.badge" :class="badgeClass(link.badge)">{{ badges[link.badge].text }}</span>
        </a>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useStore } from 'vuex';

  const { state } = useStore();

  const props = defineProps({
    section: { type: Object, required: true },
  });

  const badges = {
    new: { text: 'New', class: 'white bg-medium-green' },
    beta: { text: 'Beta', class: 'white bg-blue-full' },
  };

  const badgeClass = (badgeKey, extra = '') => `t-xs ta-c bold p-h4 br-4 m-l8 ${badges[badgeKey].class} ${extra}`.trim();

  const sectionClass = computed(() => ({
      section: true,
      active: props.section.highlighted,
    }));

  // "Shoutouts & Points" wraps onto its own line so the badge doesn't get crowded off to the right.
  const titleParts = computed(() => (
    props.section.id === 'shoutouts' ? props.section.title.replace(' & ', '\n& ').split('\n') : [props.section.title]
  ));

  const linkClass = (link, idx, sectionId) => {
    const classes = ['f', 'section-link'];
    if (idx > 0) classes.push('m-t');
    if (link.highlighted) classes.push('active');
    return classes;
  };

  const onClick = (item) => {
    const { title, href } = item;
    window.donut.track('Sidebar — Clicked Link', { title, href });
  };

  const canOpenOnHover = computed(() => {
    const links = props.section.links || [];
    return links.length > 0;
  });

  // Hovering
  const hoverStarted = ref(false);
  const isHovering = ref(false);
  const onMouseOver = () => {
    if (canOpenOnHover.value) {
      hoverStarted.value = true;
      setTimeout(() => {
        if (hoverStarted.value && props.section.links?.length) isHovering.value = true;
      }, 300);
    }
  };
  const onMouseLeave = () => {
    hoverStarted.value = false;
    isHovering.value = false;
  };

  // Transitioning
  // The nature of transitions is that it doesn't know what to do when height is "auto". So we have to kind of fake it with some JS.
  const onEnter = (el) => {
    el.style.height = '0';
    el.style.opacity = '0';
    /* eslint-disable no-unused-expressions */
    el.offsetHeight; // force reflow
    el.style.height = `${el.scrollHeight}px`;
    el.style.opacity = '1';
  };
  const onAfterEnter = (el) => {
    el.style.height = 'auto';
  };
  const onLeave = (el) => {
    el.style.height = `${el.scrollHeight}px`;
    el.style.opacity = '1';
    /* eslint-disable no-unused-expressions */
    el.offsetHeight;
    el.style.height = '0';
    el.style.opacity = '0';
  };

  const isOpen = computed(() => {
    if (isHovering.value) return true; // Sections that open on hover should open in such cases
    if (props.section.links?.length && props.section.highlighted) return true; // If a section is currently selected and has links to open
    return false;
  });
</script>

<style scoped lang="scss">
  .section-expand-enter-active,
  .section-expand-leave-active {
    transition: height 250ms ease, opacity 200ms ease;
    overflow: hidden;
  }

  .section-expand-enter-from,
  .section-expand-leave-to {
    opacity: 0;
  }
</style>
